
export type SubscriptionTier = 'alpha200' | 'alpha600' | 'alpha_unlimited';

export interface SubscriptionLimits {
  dailyProfiles: number;
  advancedFilters: boolean;
  whoViewedProfile: boolean;
  readReceipts: boolean;
  priorityPlacement: boolean;
  incognitoBrowsing: boolean;
  unlimitedLikes: boolean;
}

export const getSubscriptionLimits = (tier: SubscriptionTier): SubscriptionLimits => {
  switch (tier) {
    case 'alpha600':
      return {
        dailyProfiles: 600,
        advancedFilters: true,
        whoViewedProfile: true,
        readReceipts: true,
        priorityPlacement: false,
        incognitoBrowsing: false,
        unlimitedLikes: false,
      };
    
    case 'alpha_unlimited':
      return {
        dailyProfiles: 999, // Unlimited
        advancedFilters: true,
        whoViewedProfile: true,
        readReceipts: true,
        priorityPlacement: true,
        incognitoBrowsing: true,
        unlimitedLikes: true,
      };
    
    case 'alpha200':
    default:
      return {
        dailyProfiles: 200, // Doubled from original basic 50 to 200
        advancedFilters: false,
        whoViewedProfile: false,
        readReceipts: false,
        priorityPlacement: false,
        incognitoBrowsing: false,
        unlimitedLikes: false,
      };
  }
};

export const formatProfileLimit = (limit: number): string => {
  return limit === 999 ? '∞' : limit.toString();
};

export const getUpgradeMessage = (currentTier: SubscriptionTier): string => {
  const limits = getSubscriptionLimits(currentTier);
  
  if (currentTier === 'alpha200') {
    return `You've reached your daily limit of ${limits.dailyProfiles} profiles. Upgrade to Alpha600 for 600 profiles per day!`;
  }
  
  if (currentTier === 'alpha600') {
    return `You've reached your daily limit of ${limits.dailyProfiles} profiles. Upgrade to Alpha∞ for unlimited profiles!`;
  }
  
  return 'You have unlimited access to profiles with Alpha∞!';
};

export const getSubscriptionBenefits = (tier: SubscriptionTier): string[] => {
  const limits = getSubscriptionLimits(tier);
  const benefits: string[] = [];
  
  benefits.push(`${formatProfileLimit(limits.dailyProfiles)} profiles per day`);
  
  if (limits.advancedFilters) benefits.push('Advanced filters');
  if (limits.whoViewedProfile) benefits.push('Who viewed your profile');
  if (limits.readReceipts) benefits.push('Read receipts');
  if (limits.priorityPlacement) benefits.push('Priority placement');
  if (limits.incognitoBrowsing) benefits.push('Incognito browsing');
  if (limits.unlimitedLikes) benefits.push('Unlimited likes');
  
  return benefits;
};

export const getSubscriptionDisplayName = (tier: SubscriptionTier): string => {
  switch (tier) {
    case 'alpha200':
      return 'Alpha200';
    case 'alpha600':
      return 'Alpha600';
    case 'alpha_unlimited':
      return 'Alpha∞';
    default:
      return 'Alpha200';
  }
};

export const getSubscriptionPrice = (tier: SubscriptionTier): string => {
  switch (tier) {
    case 'alpha200':
      return 'Free';
    case 'alpha600':
      return '$14.99/month';
    case 'alpha_unlimited':
      return '$29.99/month';
    default:
      return 'Free';
  }
};
